import React, { useEffect, useState } from 'react'
import {
  Banner,
  Box,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  Pagination,
  PaginationList,
  Skeleton,
  Stack,
  StackItem,
  Tag,
  Text,
  VisuallyHidden,
} from '@indeed/ifl-components'
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHead,
  TableRow,
  TableRowHeader,
} from '@indeed/ifl-components/components/Table'
import { CheckCircle } from '@indeed/ifl-icons'
import { useFormatDateTime } from 'syft-acp-core/lib/hooks'

import { getAuthoredReasonLabel, getLineStateLabel } from './labels'
import { useSchedulingPlan } from './useSchedulingPlan'

const dateTimeFormat = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
} as const

const PLAN_LINES_PER_PAGE = 10

const formatShiftCount = (count: number) => `${count} shift${count === 1 ? '' : 's'}`

type SchedulingPlanDialogProps = {
  /** The agency's display name only — no contact details or rank. */
  agencyName: string
  jobId: number | string
  onOpenChange: (open: boolean) => void
  planId: number
  venueTimezone: string
}

/**
 * Shows one plan version's summary and its per-shift deadlines. Mount it only while open: it
 * reads the plan from Redux, so the opener is responsible for requesting it (see useSchedulingPlan).
 */
export const SchedulingPlanDialog = ({
  agencyName,
  jobId,
  onOpenChange,
  planId,
  venueTimezone,
}: SchedulingPlanDialogProps) => {
  const [page, setPage] = useState(1)
  const { plan, isLoading, isRefreshing, loadError } = useSchedulingPlan(planId)
  useEffect(() => {
    setPage(1)
  }, [planId])
  const [, formatDateTime] = useFormatDateTime()
  const format = { ...dateTimeFormat, timeZone: venueTimezone }
  const formatDate = (value: string | null) => (value ? formatDateTime(value, { format }) : '—')
  const pageStartIndex = (page - 1) * PLAN_LINES_PER_PAGE

  return (
    <Dialog size="xl" open portaled onOpenChange={onOpenChange}>
      <DialogContent onClick={event => event.stopPropagation()}>
        <DialogHeader description={agencyName ? `Job #${jobId} · Agency: ${agencyName}` : `Job #${jobId}`}>
          Allocation Booking Progress
        </DialogHeader>
        <DialogBody sx={{ textAlign: 'left' }} aria-busy={isLoading || isRefreshing}>
          {loadError && (
            <Banner status={plan ? 'warning' : 'critical'} sx={{ marginBlockEnd: 4 }}>
              {plan ? `${loadError} Showing the last loaded plan.` : loadError}
            </Banner>
          )}
          {isLoading && (
            <Box role="status">
              <VisuallyHidden>Loading allocation booking progress…</VisuallyHidden>
            </Box>
          )}
          {isRefreshing && (
            <Text as="p" role="status" level={2} variant="secondary" sx={{ marginBlockStart: 0 }}>
              Refreshing allocation booking progress…
            </Text>
          )}
          {(plan || isLoading) && (
            <Stack
              role="region"
              aria-label="Allocation booking progress details"
              aria-hidden={isLoading || undefined}
              alignment="fill"
              spacing={2}
              wrap
              sx={{
                marginBlockEnd: 5,
                '& h3': { margin: 0 },
                '& p': { marginBlockStart: 1, marginBlockEnd: 0 },
              }}
            >
              {plan
                ? [
                    { title: 'Created On', value: formatDate(plan.authored_at) },
                    {
                      title: 'Agency Status',
                      value: getAuthoredReasonLabel(plan.authored_reason),
                    },
                    {
                      title: 'Next enforced deadline',
                      value: plan.next_evaluation_at ? formatDate(plan.next_evaluation_at) : 'None scheduled',
                    },
                    { title: 'Active plan', value: `v${plan.version}` },
                  ].map(({ title, value }) => (
                    <StackItem key={title} sx={{ flex: '1 1 12rem' }}>
                      <Card>
                        <Text as="h3" level={2}>
                          {title}
                        </Text>
                        <Text as="p" level={3}>
                          {value}
                        </Text>
                      </Card>
                    </StackItem>
                  ))
                : Array.from({ length: 4 }, (_, index) => (
                    <StackItem key={index} sx={{ flex: '1 1 12rem' }}>
                      <Card>
                        <Skeleton variant="heading" size="sm" sx={{ marginBlockEnd: 2 }} />
                        <Skeleton />
                      </Card>
                    </StackItem>
                  ))}
            </Stack>
          )}

          <Stack distribution="between" alignment="baseline" sx={{ marginBlockEnd: 2 }}>
            <Text
              as="h3"
              level={2}
              variant="secondary"
              sx={{ margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              Shifts and deadlines
            </Text>
            {plan ? (
              <Text level={2} variant="secondary">
                {formatShiftCount(plan.lines.length)}
              </Text>
            ) : (
              isLoading && (
                <Box aria-hidden="true" sx={{ inlineSize: '5rem' }}>
                  <Skeleton />
                </Box>
              )
            )}
          </Stack>
          <Box sx={{ overflowX: 'auto' }}>
            <Table aria-label="Shifts and deadlines" aria-busy={isLoading || isRefreshing}>
              <TableHead>
                <TableRow>
                  <TableColumnHeader>Shift</TableColumnHeader>
                  <TableColumnHeader>Start</TableColumnHeader>
                  <TableColumnHeader>Booking deadline</TableColumnHeader>
                  <TableColumnHeader>Filled / required</TableColumnHeader>
                  <TableColumnHeader>Status</TableColumnHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {plan?.lines.slice(pageStartIndex, pageStartIndex + PLAN_LINES_PER_PAGE).map(planLine => (
                  <TableRow key={planLine.agency_shift_id}>
                    <TableRowHeader>#{planLine.shift_id}</TableRowHeader>
                    <TableCell>{formatDate(planLine.start_time)}</TableCell>
                    <TableCell>
                      {/* Visually blank by design: no deadline applies, so a date or dash would mislead. */}
                      {planLine.state === 'not_enforceable' ? (
                        <VisuallyHidden>No enforced deadline</VisuallyHidden>
                      ) : (
                        <Text as="strong">{formatDate(planLine.deadline_at)}</Text>
                      )}
                    </TableCell>
                    <TableCell>
                      {planLine.allocation_without_buffer > 0 &&
                      planLine.workers_provided >= planLine.allocation_without_buffer ? (
                        <Tag status="success">
                          <CheckCircle size="sm" aria-hidden="true" />
                          <Box as="span">
                            <VisuallyHidden>Fully filled: </VisuallyHidden>
                            {`${planLine.workers_provided} / ${planLine.allocation_without_buffer}`}
                          </Box>
                        </Tag>
                      ) : (
                        <Text as="strong">{`${planLine.workers_provided} / ${planLine.allocation_without_buffer}`}</Text>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tag
                        status={planLine.state === 'scheduled' ? 'info' : 'neutral'}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {getLineStateLabel(planLine.state)}
                      </Tag>
                    </TableCell>
                  </TableRow>
                ))}
                {isLoading &&
                  Array.from({ length: 5 }, (_, index) => (
                    <TableRow key={index}>
                      <TableRowHeader>
                        <Skeleton />
                      </TableRowHeader>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
          {plan && plan.lines.length > PLAN_LINES_PER_PAGE && (
            <Pagination
              totalItems={plan.lines.length}
              itemsPerPage={PLAN_LINES_PER_PAGE}
              page={page}
              alignment="center"
              nextAriaLabel="Next page of shifts"
              prevAriaLabel="Previous page of shifts"
              onPageChange={setPage}
              sx={{ marginBlockStart: 4 }}
            >
              <PaginationList />
            </Pagination>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
